import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Check, Tag, MessageSquare, Mail, Loader2 } from 'lucide-react';
import { useProduct, useProducts } from '../hooks/useApi';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import ColorAmbient from '../components/ColorAmbient';
import PrinterStrip from '../components/PrinterStrip';
import RegistrationMark from '../components/RegistrationMark';

const ProductDetail = () => {
  const { id } = useParams();
  const { data: product, loading } = useProduct(id);
  const { data: brandProducts } = useProducts(product ? { search: product.brand } : {});

  if (loading) {
    return (
      <main className="pt-32 pb-24 container-pn text-center min-h-screen">
        <div className="inline-flex items-center gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading product...
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-32 pb-24 container-pn text-center min-h-screen">
        <h1 className="text-4xl font-black text-[#050F2E]">Product not found</h1>
        <Link to="/shop" className="inline-block mt-6 text-[#0A2A63] font-bold hover:underline">← Back to shop</Link>
      </main>
    );
  }

  const related = (brandProducts || []).filter((p) => p.id !== product.id && p.brand === product.brand).slice(0, 3);

  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="default" intensity="subtle" />

      <div className="container-pn relative">
        {/* Breadcrumb */}
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A2A63] font-semibold mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        {/* Main */}
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Gallery */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100 p-10 h-[480px] flex items-center justify-center overflow-hidden">
              {/* Registration marks in gallery corners */}
              <RegistrationMark className="absolute top-3 left-3 opacity-40" size={22} />
              <RegistrationMark className="absolute top-3 right-3 opacity-40" size={22} />
              <RegistrationMark className="absolute bottom-3 left-3 opacity-40" size={22} />
              <RegistrationMark className="absolute bottom-3 right-3 opacity-40" size={22} />

              {/* Ambient color glow behind product */}
              <div
                className="absolute inset-0 pointer-events-none opacity-60"
                style={{
                  background:
                    'radial-gradient(circle at 30% 30%, rgba(0,183,199,0.12), transparent 55%),' +
                    'radial-gradient(circle at 70% 70%, rgba(229,0,126,0.10), transparent 55%)',
                }}
              />

              <img src={product.image} alt={product.name} className="relative max-h-full max-w-full object-contain" />
              <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 bg-[#0A2A63] text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
                <Tag className="h-3 w-3" /> {product.code}
              </div>
              <div className="absolute top-5 right-5 bg-[#FF4E50] text-[#050F2E] px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                {product.brand}
              </div>
              {/* Bottom color strip inside gallery */}
              <div className="absolute bottom-4 left-4 right-4">
                <PrinterStrip />
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Accuracy</p>
                <p className="text-lg font-black text-[#0A2A63]">{product.accuracy}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Aperture</p>
                <p className="text-lg font-black text-[#0A2A63]">{product.aperture}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Speed</p>
                <p className="text-sm font-black text-[#0A2A63]">{product.measurementSpeed}</p>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">{product.category}</p>
            <h1 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight leading-tight">{product.name}</h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{product.shortDescription}</p>

            <div className="mt-7 flex items-end gap-4">
              {product.price ? (
                <>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Price</p>
                    <p className="text-4xl font-black text-[#0A2A63]">${product.price.toLocaleString()}<span className="text-base font-bold text-slate-500">.00</span></p>
                  </div>
                </>
              ) : (
                <p className="text-2xl font-black text-[#0A2A63]">{product.priceLabel || 'On request'}</p>
              )}
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <QuoteRequestDialog product={product}>
                <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request a quote
                </Button>
              </QuoteRequestDialog>
              <a href="mailto:john@proofnation.com">
                <Button variant="outline" className="border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white rounded-full h-12 px-7 font-bold btn-transition">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact specialist
                </Button>
              </a>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="features" className="mt-10">
              <TabsList className="bg-slate-100 rounded-full p-1 h-auto">
                <TabsTrigger value="features" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0A2A63] data-[state=active]:text-white font-semibold">Main Features</TabsTrigger>
                <TabsTrigger value="specs" className="rounded-full px-5 py-2 data-[state=active]:bg-[#0A2A63] data-[state=active]:text-white font-semibold">Specifications</TabsTrigger>
              </TabsList>
              <TabsContent value="features" className="mt-6">
                <ul className="space-y-3">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-slate-700">
                      <span className="h-6 w-6 rounded-full bg-[#FF4E50]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3.5 w-3.5 text-[#FF4E50]" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </TabsContent>
              <TabsContent value="specs" className="mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(product.specs).map(([k, v]) => (
                    <div key={k} className="bg-slate-50 rounded-xl p-4">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{k}</p>
                      <p className="text-base font-bold text-[#050F2E] mt-1">{v}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="text-3xl font-black text-[#050F2E] mb-8">Related products from {product.brand}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  to={`/shop/${p.id}`}
                  key={p.id}
                  className="group bg-white rounded-3xl overflow-hidden border border-slate-100 hover:border-[#0A2A63] hover:shadow-xl btn-transition"
                >
                  <div className="h-56 bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center">
                    <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" />
                  </div>
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#FF4E50] font-bold">{p.code}</p>
                    <p className="text-sm font-bold mt-1 text-[#050F2E] line-clamp-2">{p.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
